// MfgMetaverse.jsx
// Front-end only "Mfg Metaverse"
// Place in src/components/ and import: `import MfgMetaverse from './components/MfgMetaverse'` then use <MfgMetaverse />
// Features:
// - Virtual village map (grid of plots) where players can buy plots and place crops/decorations
// - Marketplace: create listings, buy listings (local simulated economy)
// - Avatar customization (name, hat color)
// - Trade history / activity feed
// - Seasonal events and daily bonus
// - LocalStorage persistence (no backend required)
// - Responsive layout for mobile and desktop

import React, { useEffect, useMemo, useState } from 'react';

const MAP_ROWS = 6;
const MAP_COLS = 8;
const START_COINS = 500;
const PLOT_BASE_PRICE = 120;
const ITEMS = [
  { id: 'wheat_seed', name: 'Wheat Seed', price: 12, icon: '🌾' },
  { id: 'corn_seed', name: 'Corn Seed', price: 18, icon: '🌽' },
  { id: 'tomato_seed', name: 'Tomato Seed', price: 14, icon: '🍅' },
  { id: 'fence', name: 'Fence', price: 80, icon: '🪵' },
  { id: 'tree', name: 'Fruit Tree', price: 140, icon: '🌳' }
];

function lsGet(key, fallback){ try{ const v = localStorage.getItem(key); return v? JSON.parse(v): fallback; }catch(e){ return fallback; } }
function lsSet(key, val){ try{ localStorage.setItem(key, JSON.stringify(val)); }catch(e){} }

function makeEmptyMap(){
  const grid = Array.from({length: MAP_ROWS}).map(()=> Array.from({length: MAP_COLS}).map(()=> ({ owner:null, content:null })));
  return grid;
}

export default function MfgMetaverse(){
  const [player, setPlayer] = useState(() => lsGet('ag_player', {id: 'player-'+Math.floor(Math.random()*9999), name: 'Farmer', hat:'#2e7d32'}));
  const [coins, setCoins] = useState(() => lsGet('ag_coins', START_COINS));
  const [map, setMap] = useState(() => lsGet('ag_map', makeEmptyMap()));
  const [selectedPlot, setSelectedPlot] = useState(null); // {r,c}
  const [market, setMarket] = useState(() => lsGet('ag_market', []));
  const [feed, setFeed] = useState(() => lsGet('ag_feed', []));
  const [inventory, setInventory] = useState(() => lsGet('ag_inv', {wheat_seed:2,corn_seed:1}));
  const [seasonIndex, setSeasonIndex] = useState(() => lsGet('ag_season', 0));
  const seasons = ['Spring','Summer','Monsoon','Winter'];
  const [helpVisible, setHelpVisible] = useState(false);

  // persist
  useEffect(()=> lsSet('ag_player', player), [player]);
  useEffect(()=> lsSet('ag_coins', coins), [coins]);
  useEffect(()=> lsSet('ag_map', map), [map]);
  useEffect(()=> lsSet('ag_market', market), [market]);
  useEffect(()=> lsSet('ag_feed', feed), [feed]);
  useEffect(()=> lsSet('ag_inv', inventory), [inventory]);
  useEffect(()=> lsSet('ag_season', seasonIndex), [seasonIndex]);

  const seasonName = seasons[seasonIndex % seasons.length];

  // helpers
  function announce(text){ const entry = { ts: Date.now(), text }; setFeed(f => [entry, ...f].slice(0,80)); }

  function buyPlot(r,c){
    const plot = map[r][c]; if(plot.owner) return; const price = PLOT_BASE_PRICE + (r+c)*10;
    if(coins < price){ announce('Not enough coins to buy this plot.'); return; }
    setCoins(cn => cn - price);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].owner = player.id; nm[r][c].content = { type: 'empty' }; return nm; });
    announce(`${player.name} bought plot (${r+1},${c+1}) for ₹${price}`);
  }

  function placeItem(r,c, itemId){
    const item = ITEMS.find(it => it.id===itemId); if(!item) return; if(coins < item.price){ announce('Not enough coins to buy item.'); return; }
    setCoins(cn => cn - item.price);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type: 'item', id: itemId, placedBy: player.id }; return nm; });
    announce(`${player.name} placed ${item.name} on plot (${r+1},${c+1})`);
  }

  function plantCrop(r,c, seedId){
    const seed = ITEMS.find(it => it.id===seedId); if(!seed) return; if(!inventory[seedId] || inventory[seedId] <=0){ announce('No seeds in inventory. Buy from market.'); return; }
    setInventory(inv => ({...inv, [seedId]: Math.max(0, (inv[seedId]||0)-1)}));
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type:'crop', id: seedId, plantedOn: Date.now(), growth: 0 }; return nm; });
    announce(`${player.name} planted ${seed.name} at (${r+1},${c+1})`);
  }

  function harvest(r,c){ const cell = map[r][c]; if(!cell || !cell.content) return; if(cell.content.type !== 'crop') { announce('Nothing to harvest.'); return; }
    // simplified growth -> immediate harvest yields
    const cropDef = ITEMS.find(it => it.id===cell.content.id);
    const qty = Math.max(1, Math.floor(1 + Math.random()*3)); const gain = qty * Math.floor(cropDef.price * 1.5);
    setCoins(cn => cn + gain);
    setMap(m => { const nm = m.map(row => row.map(cell => ({...cell}))); nm[r][c].content = { type:'empty' }; return nm; });
    announce(`${player.name} harvested ${qty} ${cropDef.name} and earned ₹${gain}`);
  }

  function createListing(itemId, price){ const id = 'L'+Math.random().toString(36).slice(2,8); const listing = { id, seller: player.id, itemId, price, ts: Date.now() }; setMarket(m=> [listing, ...m].slice(0,50)); announce(`${player.name} listed ${itemId} for ₹${price}`); }

  function buyListing(listingId){ const lst = market.find(l => l.id===listingId); if(!lst) return; if(coins < lst.price){ announce('Not enough coins to buy listing.'); return; }
    // transfer
    setCoins(c => c - lst.price);
    setMarket(m => m.filter(x => x.id !== listingId));
    setInventory(inv => ({...inv, [lst.itemId]: (inv[lst.itemId]||0) + 1 }));
    announce(`${player.name} bought ${lst.itemId} for ₹${lst.price}`);
  }

  function quickBuyFromShop(itemId){ const item = ITEMS.find(it => it.id===itemId); if(!item) return; if(coins < item.price){ announce('Not enough coins to buy item.'); return; } setCoins(c => c - item.price); setInventory(inv => ({...inv, [itemId]: (inv[itemId]||0) + 1 })); announce(`${player.name} bought ${item.name} from shop for ₹${item.price}`); }

  function visitRandom(){ // simulate visiting other NPC farms — collect small bonus
    const bonus = Math.floor(Math.random()*40)+10; setCoins(c=> c + bonus); announce(`${player.name} visited a neighbor and earned ₹${bonus} by helping out.`); }

  function changeName(n){ setPlayer(p => ({...p, name: n})); announce(`Player changed name to ${n}`); }

  function nextSeason(){ setSeasonIndex(i => (i+1) % seasons.length); announce(`Season changed to ${seasons[(seasonIndex+1)%seasons.length]}`); }

  // small derived helpers
  const ownedPlots = useMemo(()=> { let c=0; map.forEach(row=>row.forEach(cell=>{ if(cell.owner === player.id) c++; })); return c; }, [map, player]);

  function resetAll(){ if(!window.confirm('Reset Mfg Metaverse?')) return; localStorage.clear(); window.location.reload(); }

  function showHelp(){}

  return (
    <div style={{padding:16, maxWidth:1200, margin:'0 auto', fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial'}}>
      <style>{`
        .top{display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap}
        .panel{background:#fff; padding:10px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.06)}
        .map{display:grid; gap:8}
        .plot{width:100%; padding-top:100%; position:relative; border-radius:8px; overflow:hidden}
        .plot-inner{position:absolute; inset:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; border-radius:6px}
        .sidebar{width:320px}
        @media(max-width:980px){ .layout{grid-template-columns:1fr} .sidebar{width:100%} }
      `}</style>

      <div className="flex flex-col mt-4">
        <div style={{display:'flex', gap:12, alignItems:'center'}}>
          <h2 style={{margin:0}}>🪺 Mfg Metaverse</h2>
          <div className="panel">Player: <strong>{player.name}</strong></div>
          <div className="panel">Coins: <strong>₹{coins}</strong></div>
          <div className="panel">Season: <strong>{seasonName}</strong></div>
        </div>

        <div className="mt-4" style={{display:'flex', gap:8}}>
          <button onClick={()=> setSelectedPlot(null)}>Home</button>
          <button onClick={visitRandom}>Visit Neighbor</button>
          <button onClick={nextSeason}>Next Season</button>
          <button onClick={resetAll}>Reset</button>
          <button onClick={() => setHelpVisible(v => !v)}>
            {helpVisible ? 'Hide Help' : 'Show Help'}
          </button>
        </div>
      </div>

      {helpVisible && (
        <div className='bg-white rounded-lg px-4 py-2 mt-4'>
          <h4 className='text-black'>Mfg Metaverse Help & Tutorial</h4>
          <p>Welcome to the Mfg Metaverse! Here’s how to play:</p>
          <ul style={{paddingLeft:16}}>
            <li>🟩 Click on a plot to select it.</li>
            <li>💰 Buy plots you don’t own; manage your coins wisely.</li>
            <li>🌱 Plant seeds from your inventory, wait or harvest immediately.</li>
            <li>🪵 Place items like fences or trees to decorate your farm.</li>
            <li>🏪 Use the Quick Shop or Marketplace to buy/sell items.</li>
            <li>🧑‍🌾 Visit neighbors to earn small bonuses.</li>
            <li>🌸 Seasons change—plan crops accordingly.</li>
            <li>📈 Check your Profile for owned plots and quick actions.</li>
            <li>💡 Tips: Save coins, grow crops efficiently, and watch your farm thrive!</li>
          </ul>
          <p style={{fontSize:12, color:'#666'}}>Tip: You can toggle this help anytime.</p>
        </div>
      )}

    <div style={{display:'grid', gridTemplateColumns:'1fr', gap:12, marginTop:12}} className="layout">
      <div>
        <div className="panel">
          <h3 className='text-blue-700'>Village Map</h3>
          <div style={{display:'grid', gridTemplateColumns:`repeat(${MAP_COLS}, minmax(0, 1fr))`, gap:8, overflowX:'auto'}} className="map">
            {map.map((row,r) => row.map((cell,c) => (
              <div key={`${r}-${c}`} className="plot panel" onClick={() => setSelectedPlot({r,c})}>
                <div className="plot-inner" style={{background: cell.owner ? (cell.owner===player.id? '#f0fff0':'#fff7e6') : '#fbfbfb'}}>
                  <div style={{fontSize:18}}>{cell.content ? (cell.content.type === 'crop' ? ITEMS.find(it=>it.id===cell.content.id)?.icon : (cell.content.type==='item' ? ITEMS.find(it=>it.id===cell.content.id)?.icon : '🧑‍🌾')) : '🟩'}</div>
                  <div style={{fontSize:11, marginTop:6}}>{cell.owner ? (cell.owner===player.id? 'You': 'Resident') : 'Unowned'}</div>
                  <div style={{position:'absolute', left:6, top:6, fontSize:11, color:'#666'}}>{r+1},{c+1}</div>
                </div>
              </div>
            ))) }
          </div>
        </div>

        <div style={{marginTop:12}} className="panel">
          <h4 className='text-white'>Quick Shop</h4>
          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
            {ITEMS.map(it => (
              <div key={it.id} className="panel" style={{display:'flex', gap:8, alignItems:'center'}}>
                <div style={{fontSize:20}}>{it.icon}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700}}>{it.name}</div>
                  <div style={{fontSize:12}}>₹{it.price}</div>
                </div>
                <button onClick={() => quickBuyFromShop(it.id)}>Buy</button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{marginTop:12}}>
      <div className='bg-white rounded-lg px-4 py-2'>
          <h4 className='text-black'>Plot Actions</h4>
          {selectedPlot ? (
              <div>
                <div className="text-black">Selected: <strong>{selectedPlot.r+1},{selectedPlot.c+1}</strong></div>
                <div style={{marginTop:8}}>
                    {map[selectedPlot.r][selectedPlot.c].owner ? (
                    map[selectedPlot.r][selectedPlot.c].owner === player.id ? (
                        <div style={{display:'grid flex-row', gap:8}}>
                        <button onClick={() => harvest(selectedPlot.r, selectedPlot.c)}>Harvest</button>
                        <div style={{display:'flex flex-row', gap:8, flexWrap:'wrap'}}>
                            {ITEMS.map(it => <button key={it.id} onClick={() => placeItem(selectedPlot.r, selectedPlot.c, it.id)}>{it.icon} {it.name}</button>)}
                        </div>
                        <div className="text-black" style={{marginTop:8}}>Inventory:</div>
                        <ul className="text-black" style={{paddingLeft:18}}>{Object.keys(inventory).map(k=> <li key={k}>{ITEMS.find(it=>it.id===k)?.name || k}: {inventory[k]}</li>)}</ul>
                        </div>
                    ) : (
                        <div>This plot owned by another resident. You can make an offer in marketplace.</div>
                    )
                    ) : (
                    <div style={{display:'flex', gap:8}}>
                        <button onClick={() => buyPlot(selectedPlot.r, selectedPlot.c)}>Buy Plot (₹{PLOT_BASE_PRICE + (selectedPlot.r+selectedPlot.c)*10})</button>
                    </div>
                    )}
              </div>
            </div>
          ) : (
              <div className='text-black'>Select a plot on the map to see actions.</div>
          )}
        </div>

        <div className='mt-2'>     
          <div className='panel mb-2'>
            <h4 className='text-black'>Marketplace</h4>
            <div style={{display:'grid', gap:8}}>
              <div style={{display:'flex', gap:8}}>
                <select id="mk_item" defaultValue={ITEMS[0].id} style={{flex:1}}>
                  {ITEMS.map(it=> <option key={it.id} value={it.id}>{it.name}</option>)}
                </select>
                <input id="mk_price" defaultValue={20} style={{width:80, height:36}} />
                <button onClick={() => { const itemId = document.getElementById('mk_item').value; const p = parseInt(document.getElementById('mk_price').value || '0'); createListing(itemId, p); }}>List</button>
              </div>

              <div style={{maxHeight:220, overflow:'auto'}}> 
                {market.length === 0 ? <div className='text-black'>No market listings</div> : market.map(l => (
                  <div key={l.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8}} className="panel">
                    <div><strong>{ITEMS.find(it=>it.id===l.itemId)?.name}</strong><div style={{fontSize:12}}>Seller: {l.seller}</div></div>
                    <div style={{textAlign:'right'}}>
                      <div>₹{l.price}</div>
                      <div style={{marginTop:6}}><button className="w-24" onClick={() => buyListing(l.id)}>Buy</button></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        
          <div className='panel mb-2'>  
            <h4 className='text-black mt-2 mb-2'>Activity Feed</h4>
            <div style={{maxHeight:220, overflow:'auto'}}>
              {feed.length === 0 ? <div className='text-black'>No activity yet</div> : feed.map((f,i)=> <div key={i} className="text-black" style={{padding:'6px 0', borderBottom:'1px dashed #eee'}}>{new Date(f.ts).toLocaleTimeString()} — {f.text}</div>)}
            </div>
          </div>

          <div className='panel'>  
            <h4 className='text-black mt-2'>Profile</h4>
            <div style={{display:'flex', gap:8, alignItems:'center'}}>
              <input className="bg-black text-white h-8 px-2" value={player.name} onChange={e=> changeName(e.target.value)} style={{flex:1}} />
            </div>
            <div className='text-black mt-2'>Owned plots: <strong>{ownedPlots}</strong></div>
            <div className='text-black mt-2'>Quick actions:</div>
            <div style={{display:'flex', gap:8, marginTop:8}}>
              <button onClick={() => { setCoins(c=>c+50); announce(`${player.name} claimed daily bonus ₹50`); }}>Daily Bonus</button>
              <button onClick={() => { setCoins(c=>c-30); announce(`${player.name} donated ₹30 to village fund`); }}>Donate ₹30</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer className='bg-gray-400 mt-12'>Mfg Metaverse — a virtual village & trading hub. Want multiplayer, avatars, or map skins added next?</footer>
  </div>
  );
}
